import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Box,
  TextField,
  Switch,
  IconButton,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog,
  Collapse,
  FormControlLabel,
  Snackbar,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { supabase } from '../supabaseClient';
import { useSecteur } from '../contexts/SecteurContext';
import {
  format,
  startOfMonth,
  endOfMonth,
  subMonths,
  parseISO,
  startOfWeek,
  endOfWeek,
  subWeeks,
  isSameDay,
  eachDayOfInterval,
} from 'date-fns';
import { Add as AddIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { FULL_DAY_HOURS } from '../config/timeConstants';
import { calculateReleveRate } from '../utils/releveRateCalculator';
import { formatDuration } from '../utils/timeUtils';
import HoursInput from '../components/HoursInput';
import PointageFilters from '../components/pointage/PointageFilters';
import PointageForm from '../components/pointage/PointageForm';
import PointageStats from '../components/pointage/PointageStats';
import PointageCalendar from '../components/pointage/PointageCalendar';
import PointageFavorites from '../components/pointage/PointageFavorites';
import EmailPreview from '../components/email/EmailPreview';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import PointageMensuelMobile from './PointageMensuelMobile';

const PointageMensuel = () => {
  const { user, roles } = useAuth();  // Mettre à jour pour inclure roles
  const { selectedSecteur } = useSecteur();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [releveCodes, setReleveCodes] = useState([]);
  const [activities, setActivities] = useState([]);
  const [pointages, setPointages] = useState([]);
  const [allPointages, setAllPointages] = useState([]); // Nouveau state pour stocker tous les pointages
  const [formData, setFormData] = useState({
    userId: '',
    releveCodeId: '',
    activityId: '',
    description: '',
    hours: '',
    dates: [],  // Changement pour supporter plusieurs dates
    on_report: false
  });

  // État pour les favoris
  const [favoritePointages, setFavoritePointages] = useState([]);

  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
    duration: 6000
  });

  // États pour le menu contextuel
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPointage, setSelectedPointage] = useState(null);

  // État pour le dialogue de modification
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editedPointage, setEditedPointage] = useState(null);

  // État pour gérer l'affichage des week-ends
  const [showWeekends, setShowWeekends] = useState(false);

  const [releveRates, setReleveRates] = useState({
    monthly: null,
    rolling: null
  });

  // Ajouter ce state avec les autres states en haut du composant
  const [showFavorites, setShowFavorites] = useState(false);

  // Ajouter dans la section des states au début du composant
  const [selectedReleveCodes, setSelectedReleveCodes] = useState([]);
  const [codeReleveOptions, setCodeReleveOptions] = useState([]);

  // Ajouter ces états au début du composant avec les autres states
  const [showCodesReleve, setShowCodesReleve] = useState(false);
  const [showEmailReport, setShowEmailReport] = useState(false);

  // Ajouter ces états avec les autres au début du composant
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const [pendingSubmission, setPendingSubmission] = useState(null);
  const [isSubmissionBlocked, setIsSubmissionBlocked] = useState(false);

  // Définir showSnackbar avec useCallback
  const showSnackbar = useCallback((message, severity = 'success', duration = 6000) => {
    setSnackbar({
      open: true,
      message,
      severity,
      duration
    });
  }, []); // pas de dépendances car setSnackbar est stable (fourni par useState)

  // Snackbar close handler
  const handleCloseSnackbar = useCallback((event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  }, []); // pas de dépendances car setSnackbar est stable

  // Fonction pour récupérer les pointages
  const fetchPointages = useCallback(async () => {
    if (!selectedUser || !selectedSecteur || !selectedDate) {
      console.warn('Utilisateur, secteur ou date non sélectionné');
      setPointages([]);
      return;
    }

    try {
      // Créer une date pour le premier jour du mois
      const firstDayOfMonth = startOfMonth(selectedDate);

      // Déterminer le premier jour à afficher
      let startDate;
      if (showWeekends) {
        // Si on montre les week-ends, commencer à partir du premier lundi avant le début du mois
        startDate = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 });
      } else {
        // Si on cache les week-ends, gérer différemment
        const daysInMonth = eachDayOfMonth(selectedDate);
        const firstVisibleDay = daysInMonth.find(day => ![0, 6].includes(day.getDay()));

        // Si le premier jour visible est loin du début du mois, ajouter une semaine
        if (firstVisibleDay && firstVisibleDay.getDate() > 7) {
          startDate = startOfWeek(subWeeks(firstDayOfMonth, 1), { weekStartsOn: 1 });
        } else {
          startDate = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 });
        }
      }

      // Créer une date pour le dernier jour du mois
      const lastDayOfMonth = endOfMonth(selectedDate);

      // Déterminer le dernier jour à afficher
      const endDate = endOfWeek(lastDayOfMonth, { weekStartsOn: 1 });

      // Convertir les dates en format ISO pour la requête Supabase
      const startDateISO = format(startDate, 'yyyy-MM-dd');
      const endDateISO = format(endDate, 'yyyy-MM-dd');

      // Récupérer tous les mandats de l'utilisateur pour ce secteur
      const { data: mandateData, error: mandateError } = await supabase
        .from('user_mandates')
        .select('id')
        .eq('user_id', selectedUser)
        .eq('sector_id', selectedSecteur);

      if (mandateError) throw mandateError;

      if (!mandateData || mandateData.length === 0) {
        console.warn('Aucun mandat trouvé pour cet utilisateur dans ce secteur');
        setPointages([]);
        return;
      }

      // Récupérer tous les IDs des mandats
      const mandateIds = mandateData.map(mandate => mandate.id);

      // Requête Supabase pour récupérer les pointages de tous les mandats
      const { data, error } = await supabase
        .from('pointage')
        .select(`
          *,
          releve_codes (code, color, monthly_limit),
          activities (name)
        `)
        .in('user_mandate_id', mandateIds)
        .gte('date', startDateISO)
        .lte('date', endDateISO)
        .order('date', { ascending: true });

      if (error) throw error;

      // Ajout de la couleur de fond pour chaque pointage
      const pointagesAvecCouleur = data.map(pointage => ({
        ...pointage,
        backgroundColor: pointage.releve_codes?.color // Assurez-vous que la couleur est récupérée
      }));
      setPointages(pointagesAvecCouleur);
    } catch (error) {
      console.error('Erreur lors de la récupération des pointages:', error);
      showSnackbar('Erreur de chargement des pointages', 'error');
      setPointages([]);
    }
  }, [selectedUser, selectedSecteur, selectedDate, showSnackbar, showWeekends]);

  // Fetch users when sector changes
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        if (!selectedSecteur) {
          console.warn('Aucun secteur sélectionné');
          setUsers([]);
          return;
        }

        // Récupérer les utilisateurs avec leurs mandats actifs dans le secteur
        const { data, error } = await supabase
          .from('user_mandates')
          .select(`
            users (
              id, 
              full_name
            ),
            id,
            sector_id,
            is_active
          `)
          .eq('sector_id', selectedSecteur)
          .eq('is_active', true);

        if (error) throw error;

        // Transformer les données pour obtenir une liste d'utilisateurs uniques
        const usersWithActiveMandates = data
          ?.map(mandate => ({
            id: mandate.users.id,
            full_name: mandate.users.full_name
          }))
          .filter((user, index, self) =>
            index === self.findIndex((u) => u.id === user.id)
          ) || [];

        setUsers(usersWithActiveMandates);

        // Sélectionner l'utilisateur connecté s'il est disponible
        if (user) {
          const connectedUser = usersWithActiveMandates.find(u => u.id === user.id);
          if (connectedUser) {
            setSelectedUser(connectedUser.id);
          }
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des utilisateurs:', error);
        showSnackbar('Erreur de chargement des utilisateurs', 'error');
      }
    };

    fetchUsers();
  }, [selectedSecteur, user, showSnackbar]);

  // Fetch activities
  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const { data, error } = await supabase
          .from('activities')
          .select('*');

        if (error) throw error;

        setActivities(data || []);
      } catch (error) {
        console.error('Erreur lors de la récupération des activités:', error);
        showSnackbar('Erreur de chargement des activités', 'error');
      }
    };

    fetchActivities();
  }, [showSnackbar]);

  // Fetch releve codes for selected user
  useEffect(() => {
    const fetchReleveCodes = async () => {
      try {
        if (!selectedUser || !selectedSecteur) {
          
          setReleveCodes([]);
          return;
        }

        // Récupérer les codes relève associés aux mandats ACTIFS
        const { data: mandateData, error: mandateError } = await supabase
          .from('user_mandates')
          .select(`
            id,
            releve_codes (
              id,
              code,
              color,
              role,
              mandate_description,
              destination,
              hours_per_month,
              monthly_limit,
              particularity,
              include_in_releve_rate
            ),
            sectors!inner (
              id
            )
          `)
          .eq('user_id', selectedUser)
          .eq('sectors.id', selectedSecteur)
          .eq('is_active', true);  // Ajouter ce filtre pour les mandats actifs

        if (mandateError) throw mandateError;

        // Récupérer les codes relève without_mandate
        const { data: withoutMandateCodes, error: withoutMandateError } = await supabase
          .from('releve_codes')
          .select('*')
          .eq('without_mandate', true);

        if (withoutMandateError) throw withoutMandateError;

        // Combiner les codes relève des mandats actifs avec ceux without_mandate
        const mandateCodes = mandateData
          ?.filter(mandate => mandate.releve_codes)
          .map(mandate => mandate.releve_codes) || [];

        const uniqueReleveCodes = [...mandateCodes, ...withoutMandateCodes]
          .filter((code, index, self) =>
            index === self.findIndex((c) => c.id === code.id)
          );

        setReleveCodes(uniqueReleveCodes || []);
      } catch (error) {
        console.error('Erreur lors de la récupération des codes relève:', error);
        showSnackbar('Erreur de chargement des codes relève', 'error');
      }
    };

    fetchReleveCodes();
  }, [selectedUser, selectedSecteur, showSnackbar]);

  // Fetch pointages for selected month and user
  useEffect(() => {
    // Wrapper pour gérer l'appel asynchrone
    const loadPointages = async () => {
      if (selectedUser && selectedSecteur && selectedDate) {
        await fetchPointages();
      }
    };

    loadPointages();
  }, [selectedUser, selectedSecteur, selectedDate, fetchPointages]);

  // Récupérer les pointages favoris
  const fetchFavoritePointages = useCallback(async () => {
    try {
      if (!selectedUser || !selectedSecteur) {
        setFavoritePointages([]);
        return;
      }

      // Récupérer les mandats de l'utilisateur
      const { data: mandateData, error: mandateError } = await supabase
        .from('user_mandates')
        .select('id')
        .eq('user_id', selectedUser)
        .eq('sector_id', selectedSecteur);

      if (mandateError) throw mandateError;

      if (!mandateData || mandateData.length === 0) {
        setFavoritePointages([]);
        return;
      }

      const mandateIds = mandateData.map(mandate => mandate.id);

      // Récupérer les pointages favoris avec toutes les informations
      const { data, error } = await supabase
        .from('pointage')
        .select(`
          *,
          releve_codes (code, color, role),
          activities (name)
        `)
        .in('user_mandate_id', mandateIds)
        .eq('is_favorite', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setFavoritePointages(data || []);
      // Ajout de la couleur de fond aux pointages favoris
      const FavoritepointagesAvecCouleur = data.map(pointage => ({
        ...pointage,
        backgroundColor: pointage.releve_codes?.color // Assurez-vous que la couleur est récupérée
      }));
      setFavoritePointages(FavoritepointagesAvecCouleur);

    } catch (error) {
      console.error('Erreur lors de la récupération des pointages favoris:', error);
      showSnackbar('Erreur de chargement des favoris', 'error');
    }
  }, [selectedUser, selectedSecteur, showSnackbar]);

  // Utiliser un effet pour charger les favoris quand l'utilisateur change
  useEffect(() => {
    if (selectedUser && selectedSecteur) {
      fetchFavoritePointages();
    }
  }, [selectedUser, selectedSecteur, fetchFavoritePointages]);

  // Fetch all pointages for the user
  useEffect(() => {
    const fetchAllPointages = async () => {
      try {
        if (!selectedUser || !selectedSecteur) {
          console.warn('Utilisateur ou secteur non sélectionné');
          setAllPointages([]);
          return;
        }

        // Pour les codes calendar, on a besoin de toute l'année calendaire
        const currentYear = selectedDate.getFullYear();
        const firstDayOfYear = new Date(currentYear, 0, 1);
        // Pour les codes annual, on prend les 12 derniers mois
        const startDateAnnual = startOfMonth(subMonths(selectedDate, 11));
        // On prend la date la plus ancienne entre les deux
        const startDate = startDateAnnual < firstDayOfYear ? startDateAnnual : firstDayOfYear;
        const endDate = endOfMonth(selectedDate);

        // Récupérer le mandat de l'utilisateur
        const { data: mandateData, error: mandateError } = await supabase
          .from('user_mandates')
          .select('id')
          .eq('user_id', selectedUser)
          .eq('sector_id', selectedSecteur)
          .limit(1);

        if (mandateError) throw mandateError;
        if (!mandateData || mandateData.length === 0) return;

        const mandateId = mandateData[0].id;

        // Requête Supabase pour récupérer TOUS les pointages sur la période étendue
        const { data, error } = await supabase
          .from('pointage')
          .select(`
            *,
            releve_codes (
              code,
              color,
              role,
              particularity
            ),
            activities (name)
          `)
          .eq('user_mandate_id', mandateId)
          .gte('date', startDate.toISOString())
          .lte('date', endDate.toISOString())
          .order('date', { ascending: true });

        if (error) throw error;

        const pointagesAvecCouleur = data.map(pointage => ({
          ...pointage,
          backgroundColor: pointage.releve_codes?.color
        }));

        setAllPointages(pointagesAvecCouleur);
      } catch (error) {
        console.error('Erreur lors de la récupération de tous les pointages:', error);
        setAllPointages([]);
      }
    };

    if (selectedUser && selectedSecteur && selectedDate) {
      fetchAllPointages();
    }
  }, [selectedUser, selectedSecteur, selectedDate]);

  // Fonction pour basculer le statut de favori d'un pointage
  const togglePointageFavoriteStatus = async (pointage) => {
    try {
      // Vérifier que le pointage est valide
      if (!pointage || !pointage.id) {
        showSnackbar('Impossible de modifier le statut favori', 'error');
        return;
      }

      // Inverser le statut favori
      const newFavoriteStatus = !pointage.is_favorite;

      // Mettre à jour le statut favori dans Supabase
      const { error } = await supabase
        .from('pointage')
        .update({ is_favorite: newFavoriteStatus })
        .eq('id', pointage.id);

      if (error) throw error;

      // Rafraîchir les pointages et les favoris
      await Promise.all([
        fetchPointages(),
        fetchFavoritePointages()
      ]);

      // Notification de succès
      showSnackbar(
        newFavoriteStatus
          ? 'Pointage ajouté aux favoris'
          : 'Pointage retiré des favoris',
        'info'
      );

      // Fermer le menu contextuel si ouvert
      handlePointageMenuClose();
    } catch (error) {
      console.error('Erreur lors de la modification du statut favori:', error);
      showSnackbar('Erreur de modification des favoris', 'error');
    }
  };

  // Gestion des dates

  // Supprimer une date

  // Handle form submission

  const processSubmission = async (submissionData) => {
    try {
      if (!submissionData.userId || !submissionData.releveCodeId || !submissionData.date) {
        throw new Error('Données de pointage incomplètes');
      }

      // Vérifier si le code est without_mandate
      const { data: releveCodeData, error: releveCodeError } = await supabase
        .from('releve_codes')
        .select('without_mandate')
        .eq('id', submissionData.releveCodeId)
        .single();

      if (releveCodeError) throw releveCodeError;

      let mandateId;

      if (releveCodeData.without_mandate) {
        // Vérifier si un mandat existe déjà pour ce code et cet utilisateur
        const { data: existingMandate, error: mandateCheckError } = await supabase
          .from('user_mandates')
          .select('id, is_active')
          .eq('user_id', submissionData.userId)
          .eq('sector_id', selectedSecteur)
          .eq('releve_code_id', submissionData.releveCodeId)
          .single();

        if (mandateCheckError && mandateCheckError.code !== 'PGRST116') {
          throw mandateCheckError;
        }

        if (existingMandate) {
          // Si le mandat existe mais est inactif, le réactiver
          if (!existingMandate.is_active) {
            const { error: updateError } = await supabase
              .from('user_mandates')
              .update({ is_active: true })
              .eq('id', existingMandate.id);

            if (updateError) throw updateError;
          }
          mandateId = existingMandate.id;
        } else {
          // Créer un nouveau mandat si aucun n'existe
          const { data: newMandate, error: createMandateError } = await supabase
            .from('user_mandates')
            .insert({
              user_id: submissionData.userId,
              sector_id: selectedSecteur,
              releve_code_id: submissionData.releveCodeId,
              is_active: false // Changé de true à false
            })
            .select()
            .single();

          if (createMandateError) throw createMandateError;
          mandateId = newMandate.id;
        }
      } else {
        // Comportement existant pour les codes standards
        const { data: mandateData, error: mandateError } = await supabase
          .from('user_mandates')
          .select('id')
          .eq('user_id', submissionData.userId)
          .eq('sector_id', selectedSecteur)
          .eq('is_active', true)
          .limit(1);

        if (mandateError) throw mandateError;
        if (!mandateData || mandateData.length === 0) {
          throw new Error('Aucun mandat trouvé pour cet utilisateur');
        }
        mandateId = mandateData[0].id;
      }

      // Préparer les données du pointage
      const pointageData = {
        user_mandate_id: mandateId,
        releve_code_id: submissionData.releveCodeId,
        activity: submissionData.activityId,
        description: submissionData.description || null,
        hours: parseFloat(submissionData.hours),
        date: submissionData.date,
        on_report: submissionData.on_report || false
      };

      // Insérer le pointage
      const { error: insertError } = await supabase
        .from('pointage')
        .insert(pointageData);

      if (insertError) throw insertError;

      // Rafraîchir les données
      await fetchPointages();
      showSnackbar('Pointage ajouté avec succès', 'success');

    } catch (error) {
      console.error('Erreur lors de l\'enregistrement du pointage:', error);
      showSnackbar(error.message || 'Erreur lors de l\'enregistrement du pointage', 'error');
      throw error;
    }
  };

  // Fonction de duplication unifiée pour remplir le formulaire
  const handleDuplicatePointage = (pointage) => {
    try {
      // Vérifier que tous les champs nécessaires sont présents
      if (!pointage || !pointage.releve_code_id || !pointage.activity) {
        showSnackbar('Impossible de dupliquer ce pointage', 'error');
        return;
      }

      // Trouver le code relève correspondant

      // Trouver l'activité correspondante

      // Mettre à jour le formulaire
      setFormData(prevData => ({
        ...prevData,
        releveCodeId: pointage.releve_code_id,
        activityId: pointage.activity,
        description: pointage.description || '',
        hours: pointage.hours.toString(),
        // Ne pas modifier la date
      }));

      // Notification de succès
      showSnackbar('Pointage prêt à être modifié', 'info');
    } catch (error) {
      console.error('Erreur lors de la préparation du pointage:', error);
      showSnackbar('Erreur lors de la préparation du pointage', 'error');
    }
  };

  // Modifier la fonction de duplication existante pour utiliser la nouvelle méthode
  const handleDuplicatePointageFromCalendar = () => {
    if (selectedPointage) {
      handleDuplicatePointage(selectedPointage);
      handlePointageMenuClose();
    }
  };

  // Calculer le total des heures

  // Fonction pour calculer les heures pointées par code pour le mois sélectionné

  const calculateHoursPerCode = useCallback(() => {
    // Définir le premier et le dernier jour du mois
    const firstDayOfMonth = startOfMonth(selectedDate);
    const lastDayOfMonth = endOfMonth(selectedDate);

    // Pour les codes "calendar", utiliser l'année calendaire courante
    const firstDayOfCalendarYear = new Date(selectedDate.getFullYear(), 0, 1);
    const lastDayOfCalendarYear = new Date(selectedDate.getFullYear(), 11, 31);

    // Pour les codes "annual", utiliser les 12 derniers mois
    const firstDayOfAnnual = startOfMonth(subMonths(selectedDate, 11));

    // Filtrer les pointages du mois sélectionné
    const monthPointages = pointages.filter(pointage => {
      const pointageDate = new Date(pointage.date);
      return pointageDate >= firstDayOfMonth && pointageDate <= lastDayOfMonth;
    });

    // Calculer les heures annuelles pour TOUS les pointages selon la périodicité
    const yearGroupedPointages = allPointages.reduce((acc, pointage) => {
      const code = pointage.releve_codes?.code || 'N/A';
      const periodicite = pointage.releve_codes?.particularity || 'mensuel';
      const pointageDate = new Date(pointage.date);
      
      if (!acc[code]) {
        acc[code] = { yearHoursLogged: 0 };
      }

      // Calculer selon la périodicité
      if (periodicite === 'calendar') {
        // Pour calendar: utiliser l'année calendaire
        if (pointageDate >= firstDayOfCalendarYear && pointageDate <= lastDayOfCalendarYear) {
          acc[code].yearHoursLogged += parseFloat(pointage.hours);
        }
      } else if (periodicite === 'annual') {
        // Pour annual: utiliser les 12 derniers mois
        if (pointageDate >= firstDayOfAnnual && pointageDate <= lastDayOfMonth) {
          acc[code].yearHoursLogged += parseFloat(pointage.hours);
        }
      }
      
      return acc;
    }, {});

    // Grouper les pointages par code de relève pour le mois
    const groupedPointages = monthPointages.reduce((acc, pointage) => {
      const code = pointage.releve_codes?.code || 'N/A';
      const periodicite = pointage.releve_codes?.particularity || 'mensuel';

      if (!acc[code]) {
        acc[code] = {
          hoursLogged: 0,
          mandate: pointage.releve_codes?.mandate || 'N/A',
          hoursExpected: 0,
          periodicite: periodicite,
          yearHoursLogged: 0
        };
      }
      acc[code].hoursLogged += pointage.hours;
      return acc;
    }, {});

    // Ajouter les heures prévues à partir de releveCodes
    releveCodes.forEach(releveCode => {
      const code = releveCode.code;
      const periodicite = releveCode.particularity || 'mensuel';

      if (groupedPointages[code]) {
        // Ajuster les heures prévues en fonction de la périodicité
        groupedPointages[code].hoursExpected = periodicite === 'variable'
          ? 0
          : (releveCode.hours_per_month
            ? (periodicite === 'annual' || periodicite === 'calendar'
              ? releveCode.hours_per_month / 12
              : releveCode.hours_per_month)
            : 0);

        // Mettre à jour la périodicité et les heures annuelles
        groupedPointages[code].periodicite = periodicite;
        groupedPointages[code].yearHoursLogged = yearGroupedPointages[code]?.yearHoursLogged || 0;
        groupedPointages[code].yearHoursExpected = (periodicite === 'annual' || periodicite === 'calendar')
          ? releveCode.hours_per_month
          : null;
      }
    });

    // Calculer le total des heures
    let totalLoggedHours = 0;
    let totalExpectedHours = 0;

    // Transformer le résultat en tableau 
    const codeDetails = Object.entries(groupedPointages)
      .map(([code, details]) => {
        const loggedHours = details.hoursLogged;
        const expectedHours = details.periodicite === 'variable'
          ? null
          : details.hoursExpected;

        totalLoggedHours += loggedHours;
        if (expectedHours !== null) {
          totalExpectedHours += expectedHours;
        }

        return {
          code,
          mandate: details.mandate,
          hoursLogged: loggedHours,
          hoursExpected: expectedHours,
          periodicite: details.periodicite,
          yearHoursLogged: details.yearHoursLogged,
          yearHoursExpected: details.yearHoursExpected
        };
      })
      // Trier par mandat et code
      .sort((a, b) => {
        if (a.mandate !== b.mandate) {
          return a.mandate.localeCompare(b.mandate);
        }
        return a.code.localeCompare(b.code);
      });

    return {
      totalLoggedHours,
      totalExpectedHours,
      codeDetails
    };
  }, [pointages, allPointages, releveCodes, selectedDate]);

  const calculateRates = useCallback(() => {
    if (!pointages.length || !releveCodes.length) return;

    // Pour le taux mensuel, utiliser uniquement les pointages du mois
    const monthStart = startOfMonth(selectedDate);
    const monthEnd = endOfMonth(selectedDate);

    const monthlyPointages = pointages.filter(pointage => {
      const pointageDate = new Date(pointage.date);
      return pointageDate >= monthStart && pointageDate <= monthEnd;
    });

    const monthlyRate = calculateReleveRate(
      monthlyPointages,
      releveCodes,
      monthStart,
      monthEnd
    );

    // Pour les 12 mois glissants, commencer par le mois actuel
    const rollingStart = startOfMonth(subMonths(selectedDate, 11)); // 11 mois en arrière + mois actuel = 12 mois
    const rollingEnd = endOfMonth(selectedDate);

    // Filtrer les pointages pour les 12 derniers mois
    const rollingPointages = allPointages.filter(pointage => {
      const pointageDate = new Date(pointage.date);
      return pointageDate >= rollingStart && pointageDate <= rollingEnd;
    });

    const rollingRate = calculateReleveRate(
      rollingPointages,
      releveCodes,
      rollingStart,
      rollingEnd
    );

    setReleveRates({
      monthly: monthlyRate,
      rolling: rollingRate
    });
  }, [pointages, allPointages, releveCodes, selectedDate]);

  // Ajouter l'effet pour déclencher le calcul
  useEffect(() => {
    calculateRates();
  }, [calculateRates]);

  // Rendu du calendrier mensuel

  // Fonction pour obtenir tous les jours du mois
  const eachDayOfMonth = (date) => {
    const start = startOfMonth(date);
    const end = endOfMonth(date);
    return eachDayOfInterval({ start, end });
  };

  // Calculer le total des heures
  const calculateDayTotal = (dayPointages) => {
    const totalHours = dayPointages.reduce((total, pointage) => total + parseFloat(pointage.hours), 0);
    const days = Math.floor(totalHours / FULL_DAY_HOURS);
    const remainingHours = totalHours % FULL_DAY_HOURS;
    const hours = Math.floor(remainingHours);
    const minutes = Math.round((remainingHours - hours) * 60);

    return {
      days,
      hours,
      minutes,
      total: totalHours
    };
  };

  // Fonction pour vérifier si un jour est un week-end

  // Fonction pour ouvrir le menu contextuel
  const handlePointageMenuOpen = (event, pointage) => {
    if (event) {
      setAnchorEl(event.currentTarget || event);
    }
    setSelectedPointage(pointage);
  };

  // Fonction pour fermer le menu contextuel
  const handlePointageMenuClose = () => {
    setAnchorEl(null);
    setSelectedPointage(null);
  };

  // Fonction pour modifier un pointage depuis le menu



  // Nouvelle méthode de suppression de pointage depuis le menu contextuel
  const handleContextMenuDeletePointage = async () => {
    try {
      // Fermer immédiatement le menu contextuel
      handlePointageMenuClose();

      // Vérification de la présence du pointage à supprimer
      if (!selectedPointage || !selectedPointage.id) {
        console.warn('Tentative de suppression d\'un pointage invalide');
        showSnackbar('Impossible de supprimer ce pointage', 'error');
        return;
      }

      // Suppression du pointage dans Supabase
      const { error } = await supabase
        .from('pointage')
        .delete()
        .eq('id', selectedPointage.id);

      // Gestion des erreurs de suppression
      if (error) {
        console.error('Erreur lors de la suppression du pointage:', error);
        showSnackbar(`Erreur de suppression : ${error.message}`, 'error');
        return;
      }

      // Actualiser les pointages et les pointages favoris
      await Promise.all([
        fetchPointages(),
        fetchFavoritePointages()
      ]);

      // Afficher un message de succès
      showSnackbar('Pointage supprimé avec succès', 'success');

    } catch (catchError) {
      // Log de toute erreur inattendue
      console.error('Erreur inattendue lors de la suppression du pointage:', catchError);
      showSnackbar('Une erreur inattendue est survenue', 'error');
    }
  };

  // Nouvelle méthode de modification de pointage depuis le menu contextuel
  const handleContextMenuEditPointage = () => {
    try {
      // Fermer d'abord le dialogue s'il est ouvert
      if (isEditDialogOpen) {
        setIsEditDialogOpen(false);
      }

      // Petit délai pour s'assurer que le dialogue est bien fermé
      setTimeout(() => {
        // Trouver le code relève correspondant
        const matchingReleveCode = releveCodes.find(
          code => code.id === selectedPointage.releve_code_id
        );

        // Trouver l'activité correspondante
        const matchingActivity = activities.find(
          activity => activity.id === selectedPointage.activity
        );

        // Vérifier si le code relève et l'activité sont valides
        if (!matchingReleveCode || !matchingActivity) {
          console.error('Impossible de trouver le code relève ou l\'activité correspondante');
          showSnackbar('Erreur lors de la préparation de modification', 'error');
          return;
        }

        // Préparer les données pour l'édition
        const pointageToEdit = {
          id: selectedPointage.id,
          releveCodeId: selectedPointage.releve_code_id,
          activityId: selectedPointage.activity,
          date: new Date(selectedPointage.date),
          hours: selectedPointage.hours,
          description: selectedPointage.description || '',
          on_report: selectedPointage.on_report // Add this line
        };

        // Ouvrir le dialogue d'édition
        setIsEditDialogOpen(true);
        setEditedPointage(pointageToEdit);
      }, 50);  // Petit délai pour permettre la réinitialisation

    } catch (catchError) {
      // Log de toute erreur inattendue
      console.error('Erreur inattendue lors de la préparation de modification du pointage:', catchError);
      showSnackbar('Une erreur inattendue est survenue', 'error');
    }
  };

  // Actions du menu





  // Fonction pour ouvrir le dialogue de modification

  // Fonction pour fermer le dialogue de modification
  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditedPointage(null);
  };

  // Fonction pour sauvegarder les modifications du pointage
  const handleSaveEditedPointage = async () => {
    try {
      // Validation des données
      if (!editedPointage.releveCodeId || !editedPointage.hours) {
        showSnackbar('Veuillez remplir tous les champs requis', 'error');
        return;
      }

      // Préparation des données à mettre à jour
      const updatedPointage = {
        date: format(editedPointage.date, 'yyyy-MM-dd'),
        releve_code_id: editedPointage.releveCodeId,
        activity: editedPointage.activityId, // Utiliser 'activity' au lieu de 'activityId'
        hours: parseFloat(editedPointage.hours),
        description: editedPointage.description || null,
        on_report: editedPointage.on_report
      };

      // Mise à jour dans Supabase
      const { error } = await supabase
        .from('pointage')
        .update(updatedPointage)
        .eq('id', editedPointage.id);

      // Gestion des erreurs
      if (error) {
        console.error('Erreur lors de la modification du pointage:', error);
        showSnackbar(`Erreur de modification : ${error.message}`, 'error');
        return;
      }

      // Actualiser les pointages et les pointages favoris
      await Promise.all([
        fetchPointages(),
        fetchFavoritePointages()
      ]);

      // Fermer le dialogue et afficher une notification
      handleCloseEditDialog();
      showSnackbar('Pointage modifié avec succès', 'success');

    } catch (error) {
      console.error('Erreur inattendue lors de la modification du pointage:', error);
      showSnackbar('Une erreur inattendue est survenue', 'error');
    }
  };

  // Fonction pour obtenir la couleur de fond en fonction du code relève

  // Fonction pour déterminer la couleur de texte en fonction de la couleur de fond
  const getTextColorForBackground = (backgroundColor) => {
    // Vérifier si la couleur de fond est définie
    if (!backgroundColor) return '#000000'; // Couleur par défaut si non définie

    // Convertir la couleur hexadécimale en valeurs RGB
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calculer la luminosité
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Retourner noir ou blanc selon la luminosité
    return luminance > 0.5 ? '#000000' : '#ffffff';
  };

  // Fonction pour formater le texte du pointage

  // Fonction pour formater le texte d'un pointage favori
  const formatFavoritePointageDetails = (pointage) => {
    return [
      `Code : ${pointage.releve_codes?.code || 'N/A'}`,
      `Activité : ${pointage.activities?.name || 'N/A'}`,
      `Description : ${pointage.description || 'Aucune'}`,
      `Heures : ${pointage.hours || '0'}h`,
      `Date : ${pointage.date ? format(new Date(pointage.date), 'dd/MM/yyyy') : 'N/A'}`
    ].join('\n');
  };

  // Gestion du clic sur un numéro de jour dans le calendrier

  // Fonction pour rendre le contenu d'un jour
  const renderDayContent = (day) => {
    if (!selectedUser || !selectedDate) {
      return null;
    }

    const dayPointages = pointages.filter(pointage =>
      isSameDay(parseISO(pointage.date), day)
    );

    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        height: '100%',
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 1 
        }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleQuickAddPointage(day)}
            sx={{
              height: '24px',
              minWidth: 'auto',
              borderRadius: 0,
              fontSize: '0.8rem',
              fontWeight: 'bold',
              flex: 1,
              mr: 0.5,
              '&:hover': {
                backgroundColor: 'primary.dark'
              }
            }}
          >
            {format(day, 'd/M')}
          </Button>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleQuickAddPointage(day);
            }}
            sx={{ 
              padding: '2px',
              backgroundColor: 'background.paper',
              '&:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1 }}>
          {dayPointages.map((pointage) => (
            <Box
              key={pointage.id}
              sx={{
                mb: 0.5,
                p: 0.5,
                borderTop: pointage.is_favorite ? 2 : 0,
                borderBottom: pointage.on_report ? 2 : 0,
                borderColor: pointage.is_favorite ? 'warning.light' : (pointage.on_report ? 'warning.light' : pointage.backgroundColor),
                backgroundColor: pointage.backgroundColor,
                borderRadius: 1,
                color: getTextColorForBackground(pointage.backgroundColor),
                fontSize: '0.7rem',
                cursor: 'pointer'
              }}
              onClick={(e) => handlePointageMenuOpen(e, pointage)}
            >
              <Typography variant="caption" component="div" fontWeight="bold">
                {formatDuration(pointage.hours)} - {pointage.releve_codes?.code || 'N/A'}{pointage.releve_codes?.role ? ` (${pointage.releve_codes.role})` : ''}
              </Typography>
              <Typography variant="caption" component="div">
                {pointage.activities?.name || 'Activité'}
                {pointage.description ? ` : ${pointage.description}` : ''}
              </Typography>
            </Box>
          ))}
        </Box>

        {dayPointages.length > 0 && (
          <Typography
            variant="caption"
            sx={{
              pt: 0.5,
              fontWeight: 'bold',
              textAlign: 'right',
              borderTop: '1px solid',
              borderColor: 'divider',
              color: (theme) => {
                const dayTotal = calculateDayTotal(dayPointages);
                return dayTotal.total > FULL_DAY_HOURS ? theme.palette.error.main : theme.palette.text.secondary;
              }
            }}
          >
            Total: {formatDuration(calculateDayTotal(dayPointages).total)}
          </Typography>
        )}
      </Box>
    );
  };

 

  // Fonction de création rapide de pointage depuis le calendrier
  const handleQuickAddPointage = async (day) => {
    try {
      // Vérifier que les données essentielles sont présentes
      if (!formData.releveCodeId || !formData.hours) {
        showSnackbar('Veuillez sélectionner un code de relève et saisir des heures', 'warning');
        return;
      }

      // Récupérer le code relève sélectionné
      const releveCode = releveCodes.find(code => code.id === formData.releveCodeId);
      if (!releveCode) {
        showSnackbar('Code relève non trouvé', 'error');
        return;
      }

      // Vérifier les limites du code relève
      const limits = await checkCodeLimits(releveCode, parseFloat(formData.hours));

      if (!limits.canProceed) {
        showSnackbar(limits.message, 'error');
        return;
      }

      const pointageData = {
        userId: selectedUser,
        releveCodeId: formData.releveCodeId,
        activityId: formData.activityId,
        description: formData.description,
        hours: parseFloat(formData.hours),
        date: format(day, 'yyyy-MM-dd'),
        on_report: formData.on_report
      };

      if (limits.needsConfirmation) {
        showSnackbar(limits.message, 'warning');  // Ajout du warning pour dépassement
        setConfirmationMessage(
          <>
            <Typography>{limits.message}</Typography>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Code: {releveCode.code}<br />
              Date: {format(day, 'dd/MM/yyyy')}<br />
              Heures: {formatDuration(parseFloat(formData.hours))}
            </Typography>
          </>
        );
        setPendingSubmission(pointageData);
        setConfirmationDialogOpen(true);
        return;
      }

      await processSubmission(pointageData);

    } catch (error) {
      console.error('Erreur lors de l\'ajout rapide du pointage:', error);
      showSnackbar('Erreur lors de l\'ajout du pointage', 'error');
    }
  };

  // Fonction pour obtenir les jours filtrés du mois
  const getFilteredDays = useCallback(() => {
    if (!selectedDate) return [];

    const firstDayOfMonth = startOfMonth(selectedDate);
    let startDate;

    if (showWeekends) {
      startDate = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 });
    } else {
      const daysInMonth = eachDayOfMonth(selectedDate);
      const firstVisibleDay = daysInMonth.find(day => ![0, 6].includes(day.getDay()));

      if (firstVisibleDay && firstVisibleDay.getDate() > 7) {
        startDate = startOfWeek(subWeeks(firstDayOfMonth, 1), { weekStartsOn: 1 });
      } else {
        startDate = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 });
      }
    }

    const lastDayOfMonth = endOfMonth(selectedDate);
    const endDate = endOfWeek(lastDayOfMonth, { weekStartsOn: 1 });

    const days = eachDayOfInterval({ start: startDate, end: endDate });
    return showWeekends ? days : days.filter(day => ![0, 6].includes(day.getDay()));
  }, [selectedDate, showWeekends]);

  // Préparer les données pour EmailPreview
  const monthlyPlanningForEmail = useMemo(() => {
    // Définir le premier et dernier jour du mois
    const firstDayOfMonth = startOfMonth(selectedDate);
    const lastDayOfMonth = endOfMonth(selectedDate);

    // Filtrer les pointages pour ne garder que ceux du mois sélectionné
    const monthPointages = pointages.filter(pointage => {
      const pointageDate = new Date(pointage.date);
      return pointageDate >= firstDayOfMonth && pointageDate <= lastDayOfMonth;
    });

    // Grouper les pointages filtrés par code
    const groupedPointages = monthPointages.reduce((acc, pointage) => {
      const code = pointage.releve_codes?.code;
      if (!acc[code]) {
        acc[code] = { entries: [] };
      }
      acc[code].entries.push(pointage);
      return acc;
    }, {});

    return groupedPointages;
  }, [pointages, selectedDate]);

  // Ajouter après le useEffect existant qui charge les codes relève
  useEffect(() => {
    const codesPresent = Object.keys(monthlyPlanningForEmail);
    setCodeReleveOptions(codesPresent);
  }, [monthlyPlanningForEmail]);

  // Ajouter la fonction de gestion du changement
  const handleCodeReleveChange = (event) => {
    setSelectedReleveCodes(event.target.value);
  };

  const checkCodeLimits = async (releveCode, newHours) => {
    if (!releveCode) return { canProceed: true };

    // Calculer le premier et dernier jour du mois
    const firstDayOfMonth = startOfMonth(selectedDate);
    const lastDayOfMonth = endOfMonth(selectedDate);

    // Récupérer les pointages du mois pour ce code
    const monthlyPointages = pointages.filter(p => 
      p.releve_code_id === releveCode.id &&
      new Date(p.date) >= firstDayOfMonth &&
      new Date(p.date) <= lastDayOfMonth
    );

    // Calculer le total des heures du mois
    const monthlyTotal = monthlyPointages.reduce((sum, p) => sum + p.hours, 0) + newHours;

    // Vérifier la limite mensuelle si elle existe
    if (releveCode.monthly_limit && monthlyTotal > releveCode.monthly_limit) {
      return {
        canProceed: false,
        message: `Ce pointage dépasserait la limite mensuelle de ${formatDuration(releveCode.monthly_limit)}.`
      };
    }

    // Pour les codes annuels et calendaires, vérifier la dotation annuelle
    if ((releveCode.particularity === 'annual' || releveCode.particularity === 'calendar') && releveCode.hours_per_month) {
      let yearPointages;
      
      if (releveCode.particularity === 'calendar') {
        // Pour calendar, utiliser l'année calendaire
        const firstDayOfYear = new Date(selectedDate.getFullYear(), 0, 1);
        const lastDayOfYear = new Date(selectedDate.getFullYear(), 11, 31);
        
        yearPointages = allPointages.filter(p => 
          p.releve_code_id === releveCode.id &&
          new Date(p.date) >= firstDayOfYear &&
          new Date(p.date) <= lastDayOfYear
        );
      } else {
        // Pour annual, garder le comportement existant
        yearPointages = allPointages.filter(p => 
          p.releve_code_id === releveCode.id &&
          new Date(p.date).getFullYear() === selectedDate.getFullYear()
        );
      }
      
      const yearlyTotal = yearPointages.reduce((sum, p) => sum + p.hours, 0) + newHours;
      
      if (yearlyTotal > releveCode.hours_per_month) {
        return {
          canProceed: false,
          message: `Ce pointage dépasserait la dotation ${releveCode.particularity === 'calendar' ? 'de l\'année calendaire' : 'annuelle'} de ${formatDuration(releveCode.hours_per_month)}.`
        };
      }
    }

    // Pour les codes mensuels, vérifier la dotation mensuelle
    if (releveCode.particularity === 'monthly' && releveCode.hours_per_month) {
      if (monthlyTotal > releveCode.hours_per_month) {
        return {
          canProceed: true,
          needsConfirmation: true,
          message: `Ce pointage dépasserait la dotation mensuelle de ${formatDuration(releveCode.hours_per_month)}. Voulez-vous continuer ?`
        };
      }
    }

    return { canProceed: true };
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Préparer les données des pointages par jour pour la version mobile
  const pointagesByDay = useMemo(() => {
    return pointages.reduce((acc, pointage) => {
      const date = format(parseISO(pointage.date), 'yyyy-MM-dd');
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(pointage);
      return acc;
    }, {});
  }, [pointages]);

  if (isMobile) {
    return (
      <PointageMensuelMobile
        selectedDate={selectedDate}
        selectedUser={selectedUser}
        users={users}
        onUserChange={setSelectedUser}
        onDateChange={setSelectedDate}
        disabled={!roles?.can_view_all_users}
        pointages={pointages}
        filteredDays={getFilteredDays()}
        dayPointages={pointagesByDay}
        showWeekends={showWeekends}
        onQuickAdd={handleQuickAddPointage}
        onPointageClick={(pointage, position) => handlePointageMenuOpen(position, pointage)}
        onToggleWeekends={() => setShowWeekends(!showWeekends)}
        formData={formData}
        onFormChange={setFormData}
        releveCodes={releveCodes}
        activities={activities}
        selectedReleveCodes={selectedReleveCodes}
        onCodeReleveChange={handleCodeReleveChange}
        codeReleveOptions={codeReleveOptions}
        renderFavorites={() => (
          <PointageFavorites
            favoritePointages={favoritePointages}
            onDuplicatePointage={handleDuplicatePointage}
            onRemoveFavorite={togglePointageFavoriteStatus}
            formatFavoritePointageDetails={formatFavoritePointageDetails}
          />
        )}
        renderEmail={() => (
          <EmailPreview
            selectedDate={selectedDate}
            monthlyPlanning={monthlyPlanningForEmail}
            selectedReleveCodes={selectedReleveCodes}
          />
        )}
        anchorEl={anchorEl}
        selectedPointage={selectedPointage}
        onMenuClose={handlePointageMenuClose}
        onEditClick={() => {
          handleContextMenuEditPointage();
          handlePointageMenuClose();
        }}
        onDuplicateClick={() => {
          handleDuplicatePointageFromCalendar();
          handlePointageMenuClose();
        }}
        onFavoriteClick={() => {
          togglePointageFavoriteStatus(selectedPointage);
          handlePointageMenuClose();
        }}
        onDeleteClick={handleContextMenuDeletePointage}
        isEditDialogOpen={isEditDialogOpen}
        onCloseEditDialog={handleCloseEditDialog}
        editedPointage={editedPointage}
        onEditedPointageChange={setEditedPointage}
        onSaveEditedPointage={handleSaveEditedPointage}
      />
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Paper elevation={3} sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
            {/* Première ligne : Filtres et switches */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
                <PointageFilters
                  users={users}
                  selectedUser={selectedUser}
                  selectedDate={selectedDate}
                  onUserChange={setSelectedUser}
                  onDateChange={setSelectedDate}
                  disabled={!roles?.can_view_all_users}
                />
              </Box>
              
              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                alignItems: 'center',
                justifyContent: 'flex-end'
              }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showFavorites}
                      onChange={(e) => setShowFavorites(e.target.checked)}
                    />
                  }
                  label="Favoris"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showCodesReleve}
                      onChange={(e) => setShowCodesReleve(e.target.checked)}
                    />
                  }
                  label="Codes relève"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showEmailReport}
                      onChange={(e) => setShowEmailReport(e.target.checked)}
                    />
                  }
                  label="Rapport email"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showWeekends}
                      onChange={(e) => setShowWeekends(e.target.checked)}
                    />
                  }
                  label="Week-ends"
                />
              </Box>
            </Box>

            {/* Deuxième ligne : Formulaire */}
            <Box sx={{ display: 'flex', width: '100%' }}>
              <PointageForm
                formData={formData}
                onFormChange={setFormData}
                releveCodes={releveCodes}
                activities={activities}
              />
            </Box>

            {/* Contenu des panels */}
            <Box sx={{ width: '100%' }}>
              <Collapse in={showFavorites}>
                <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                  <PointageFavorites
                    favoritePointages={favoritePointages}
                    onDuplicatePointage={handleDuplicatePointage}
                    onRemoveFavorite={(pointage) => togglePointageFavoriteStatus(pointage)}
                    formatFavoritePointageDetails={formatFavoritePointageDetails}
                  />
                </Paper>
              </Collapse>

              <Collapse in={showCodesReleve}>
                <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Codes de relève disponibles
                  </Typography>
                  <Grid container spacing={1}>
                    {releveCodes.map(code => (
                      <Grid item xs={12} sm={6} md={4} key={code.id}>
                        <Paper 
                          elevation={1}
                          sx={{ 
                            p: 1, 
                            backgroundColor: code.color,
                            color: getTextColorForBackground(code.color)
                          }}
                        >
                          <Typography variant="subtitle2">
                            {code.code} {code.role ? `(${code.role})` : ''}
                          </Typography>
                          
                          {code.hours_per_month && (
                            <Typography variant="caption" display="block">
                              Dotation: {formatDuration(code.hours_per_month)}/
                              {code.particularity === 'annual' ? 'an' : 'mois'}
                            </Typography>
                          )}
                          
                          {code.monthly_limit && (
                            <Typography variant="caption" display="block">
                              Maximum: {formatDuration(code.monthly_limit)}
                            </Typography>
                          )}

                          <Typography 
                            variant="caption" 
                            display="block" 
                            sx={{ 
                              mt: 0.5,
                              borderTop: '1px solid',
                              borderColor: 'rgba(255, 255, 255, 0.2)',
                              pt: 0.5
                            }}
                          >
                            Destination: {code.destination || 'Non spécifiée'}
                          </Typography>
                          
                          <Typography variant="caption" display="block">
                            Description: {code.mandate_description || 'Non spécifiée'}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              </Collapse>

              <Collapse in={showEmailReport}>
                <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Génération de rapport par email
                  </Typography>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel>Codes Relève à inclure</InputLabel>
                    <Select
                      multiple
                      value={selectedReleveCodes}
                      label="Codes Relève à inclure"
                      onChange={handleCodeReleveChange}
                    >
                      {codeReleveOptions.map((code) => (
                        <MenuItem key={code} value={code}>
                          {code}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <EmailPreview
                    selectedDate={selectedDate}
                    monthlyPlanning={monthlyPlanningForEmail}
                    selectedReleveCodes={selectedReleveCodes}
                  />
                </Paper>
              </Collapse>
            </Box>

            {/* Calendrier */}
            <Box sx={{ width: '100%' }}>
              <PointageCalendar
                filteredDays={getFilteredDays()}
                showWeekends={showWeekends}
                renderDayContent={renderDayContent}
                handleQuickAddPointage={handleQuickAddPointage}
                anchorEl={anchorEl}
                selectedPointage={selectedPointage}
                onMenuClose={handlePointageMenuClose}
                onEditClick={() => {
                  handleContextMenuEditPointage();
                  handlePointageMenuClose();
                }}
                onDuplicateClick={() => {
                  handleDuplicatePointageFromCalendar();
                  handlePointageMenuClose();
                }}
                onFavoriteClick={() => {
                  togglePointageFavoriteStatus(selectedPointage);
                  handlePointageMenuClose();
                }}
                onDeleteClick={handleContextMenuDeletePointage}
              />
            </Box>
          </Box>
        </Paper>
      </Grid>

      {/* Deuxième section: Stats */}
      <Grid item xs={12}>
        <Paper elevation={3} sx={{ p: 2 }}>
          <PointageStats
            releveRates={releveRates}
            hoursPerCode={calculateHoursPerCode()}
            releveCodes={releveCodes}
            onShowWarning={(message) => showSnackbar(message, 'warning')}
          />
        </Paper>
      </Grid>

      {/* Dialogue de modification */}
      <Dialog 
        open={isEditDialogOpen} 
        onClose={handleCloseEditDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            minWidth: '800px',
            maxWidth: '1000px'
          }
        }}
      >
        <DialogTitle>Modifier le pointage</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <DatePicker
              label="Date"
              value={editedPointage?.date}
              onChange={(newDate) => setEditedPointage(prev => ({
                ...prev,
                date: newDate
              }))}
              renderInput={(params) => <TextField {...params} />}
            />
            <FormControl fullWidth>
              <InputLabel>Code Relève</InputLabel>
              <Select
                value={editedPointage?.releveCodeId || ''}
                label="Code Relève"
                onChange={(e) => setEditedPointage(prev => ({
                  ...prev,
                  releveCodeId: e.target.value
                }))}
              >
                {releveCodes.map((code) => (
                  <MenuItem key={code.id} value={code.id}>
                    {code.code} {code.role ? `(${code.role})` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Activité</InputLabel>
              <Select
                value={editedPointage?.activityId || ''}
                label="Activité"
                onChange={(e) => setEditedPointage(prev => ({
                  ...prev,
                  activityId: e.target.value
                }))}
              >
                {activities.map((activity) => (
                  <MenuItem key={activity.id} value={activity.id}>
                    {activity.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Description"
              value={editedPointage?.description || ''}
              onChange={(e) => setEditedPointage(prev => ({
                ...prev,
                description: e.target.value
              }))}
              multiline
              rows={4}
              fullWidth
            />
            <HoursInput
              value={editedPointage?.hours || 0}
              onChange={(newValue) => setEditedPointage(prev => ({
                ...prev,
                hours: newValue
              }))}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editedPointage?.on_report || false}
                  onChange={(e) => setEditedPointage(prev => ({
                    ...prev,
                    on_report: e.target.checked
                  }))}
                />
              }
              label="Fait Marquant"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Annuler</Button>
          <Button onClick={handleSaveEditedPointage} color="primary">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue de confirmation */}
      <Dialog
        open={confirmationDialogOpen}
        onClose={() => {
          if (!isSubmissionBlocked) {
            setConfirmationDialogOpen(false);
            setPendingSubmission(null);
          }
        }}
      >
        <DialogTitle>Confirmation nécessaire</DialogTitle>
        <DialogContent>
          {confirmationMessage}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setConfirmationDialogOpen(false);
              setPendingSubmission(null);
            }}
            disabled={isSubmissionBlocked}
          >
            Annuler
          </Button>
          <Button 
            onClick={() => {
              if (pendingSubmission) {
                setIsSubmissionBlocked(true);
                processSubmission(pendingSubmission).finally(() => {
                  setIsSubmissionBlocked(false);
                  setConfirmationDialogOpen(false);
                  setPendingSubmission(null);
                });
              }
            }}
            color="primary"
            variant="contained"
            disabled={isSubmissionBlocked}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.duration}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: '20px !important',  // Force la position en haut
          width: '100%',
          maxWidth: '600px',
          margin: '0 auto'
        }}
      >
        <Alert 
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          elevation={6}
          sx={{
            width: '100%',
            '&.MuiAlert-error': {
              backgroundColor: 'error.dark',
              fontSize: '1.1rem',
              fontWeight: 'bold'
            },
            '&.MuiAlert-success': {
              backgroundColor: 'success.main',
              fontSize: '1rem'
            },
            '&.MuiAlert-warning': {
              backgroundColor: 'warning.dark',
              fontSize: '1rem',
              fontWeight: 'medium'
            },
            '&.MuiAlert-info': {
              backgroundColor: 'info.main',
              fontSize: '1rem',
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ...existing dialogs... */}
    </Grid>
  );
};

export default PointageMensuel;